import { SFConnection } from 'common/sf-connector';
import { IOrg, IOrgPortalEntry } from 'interfaces/IOrg.interface';
import { IPushPortalCreatePushRequestResponse, IPushRequest } from 'interfaces/IPushRequest.interface';
import { CQAppConstant } from 'app-constants';

/**
 * Client wrapper for Push Portal API endpoints.
 */
export class PushPortalService {

    /** Resolve the authenticated Axios instance for API requests. */
    private get http() {
        return SFConnection.getAxiosInstance();
    }

    /** Base path for Push Portal API endpoints */
    private get BASE() {
        return CQAppConstant.PUSH_PORTAL_SERVICE;
    }

    /**
     * List org entries for the Push Portal page.
     *
     * Supports server-side pagination and optional `search` by orgName/orgId.
     * Supports optional `orgType` filter (production/sandbox).
     * Supports optional version filtering (`filterMode` and `filterVersion`).
     * For upgrade mode, applicableUpgrades are resolved server-side from filterVersion.
     */
    async listOrgs(params?: { page?: number; pageSize?: number; search?: string; orgType?: string; filterMode?: string; filterVersion?: string }): Promise<{
        items: IOrgPortalEntry[];
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
        hasPrev: boolean;
        hasNext: boolean;
    }> {
        const qs = new URLSearchParams();
        if (params?.page !== undefined) qs.set('page', String(params.page));
        if (params?.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
        if (params?.search) qs.set('search', params.search);
        if (params?.orgType) qs.set('orgType', params.orgType);
        if (params?.filterMode) qs.set('filterMode', params.filterMode);
        if (params?.filterVersion) qs.set('filterVersion', params.filterVersion);

        const res = await this.http.get(`${this.BASE}/orgs?${qs.toString()}`);
        return res.data;
    }

    // Fetch org metadata by id.
    async getOrg(orgId: string): Promise<IOrg> {
        const res = await this.http.get(`${this.BASE}/orgs/${orgId}`);
        return res.data;
    }

    // Fetch child sandboxes for a parent org.
    async getSandboxes(orgId: string): Promise<IOrg[]> {
        const res = await this.http.get(`${this.BASE}/orgs/${orgId}/sandboxes`);
        return res.data;
    }

    // Fetch upgrade activity for a single org.
    async getOrgUpgrades(orgId: string): Promise<{ pending: any[]; failedLast24h: any[]; scheduled: any[] }> {
        const res = await this.http.get(`${this.BASE}/orgs/${orgId}/upgrades`);
        return res.data;
    }

    /**
     * Update org metadata fields in Push Portal.
     *
     * @param orgId - Target org id.
     * @param updates - Partial update payload (including tags).
     */
    async updateOrg(orgId: string, updates: Partial<Pick<IOrg, 'authStatus' | 'integrationUsername' | 'instanceUrl' | 'tagNames'>>): Promise<IOrg> {
        const res = await this.http.patch(`${this.BASE}/orgs/${orgId}`, updates);
        return res.data;
    }

    /**
     * Delete (soft delete) a registered org by orgId.
     *
     * Marks the org (and its sandboxes) as deleted on the backend so they no longer appear in lists.
     */
    async deleteOrg(orgId: string): Promise<void> {
        await this.http.delete(`${this.BASE}/orgs/${encodeURIComponent(orgId)}`);
    }

    /**
     * Create a push request for one or more orgs.
     *
     * The `module` payload is persisted and later used by Upgrade Activity Scheduled tab.
     */
    async createPushRequest(payload: {
        orgIds: string[];
        createdBy: string;
        createdByEmail?: string;
        module?: any;
        scheduledTime?: string;
    }): Promise<IPushPortalCreatePushRequestResponse> {
        const res = await this.http.post(`${this.BASE}/push-requests`, payload);
        return res.data;
    }

    /** List push requests, optionally filtered by orgId. */
    async listPushRequests(filters?: { orgId?: string }): Promise<IPushRequest[]> {
        const params = new URLSearchParams();
        if (filters?.orgId) params.append('orgId', filters.orgId);
        const res = await this.http.get(`${this.BASE}/push-requests?${params.toString()}`);
        return res.data;
    }

    /** Fetch a single push request by requestId. */
    async getPushRequest(requestId: string): Promise<IPushRequest> {
        const res = await this.http.get(`${this.BASE}/push-requests/${requestId}`);
        return res.data;
    }

    /**
     * Cancel either a whole push request or selected child orgs.
     *
     * When `orgIds` is omitted, the backend cancels all scheduled child orgs.
     * The backend computes the remaining orgIds/status from its own fresh read of
     * the request, so concurrent cancellations can't overwrite each other.
     *
     * @param cancelledBy - CQ Mgmt username of whoever is performing the cancellation, used for the cancellation notification email.
     */
    async cancelPushRequest(requestId: string, orgIds?: string[], cancelledBy?: string): Promise<IPushRequest> {
        const res = await this.http.patch(`${this.BASE}/push-requests/${requestId}`, {
            cancelOrgIds: orgIds && orgIds.length > 0 ? orgIds : null,
            cancelledBy,
        });
        return res.data;
    }

    /**
     * Update scheduled time of a push request.
     */
    async updatePushRequestScheduledTime(requestId: string, scheduledTime: string): Promise<IPushRequest> {
        const res = await this.http.patch(`${this.BASE}/push-requests/${requestId}`, { scheduledTime });
        return res.data;
    }

    /** Common method to fetch paginated activities by type with cursor support. */
    private async fetchActivitiesByType(
        activityType: string,
        params?: { page?: number; pageSize?: number; search?: string; cursor?: string }
    ): Promise<any> {
        const qs = new URLSearchParams();
        qs.set('type', activityType);
        if (params?.page !== undefined) qs.set('page', String(params.page));
        if (params?.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
        if (params?.search) qs.set('search', params.search);
        if (params?.cursor) qs.set('cursor', params.cursor);
        const res = await this.http.get(`${this.BASE}/activity?${qs.toString()}`);
        return res.data;
    }

    /** Fetch paginated scheduled activities with cursor support. */
    async listAllScheduledActivity(params?: { page?: number; pageSize?: number; search?: string; cursor?: string }): Promise<any> {
        return this.fetchActivitiesByType('scheduled', params);
    }

    /** Fetch paginated paused activities with cursor support. */
    async listAllPausedActivity(params?: { page?: number; pageSize?: number; search?: string; cursor?: string }): Promise<any> {
        return this.fetchActivitiesByType('paused', params);
    }

    /** Fetch paginated pending activities with cursor support. */
    async listAllPendingActivity(params?: { page?: number; pageSize?: number; search?: string; cursor?: string }): Promise<any> {
        return this.fetchActivitiesByType('pending', params);
    }

    /** Fetch paginated failed activities with cursor support. */
    async listAllFailedActivity(params?: { page?: number; pageSize?: number; search?: string; cursor?: string }): Promise<any> {
        return this.fetchActivitiesByType('failed', params);
    }

    /** Fetch Push Portal permissions for the current user. */
    async getPermissions(): Promise<{ canView: boolean; canSchedule: boolean; canRegister: boolean }> {
        const res = await this.http.get(`${this.BASE}/permissions`);
        return res.data;
    }

    /**
     * Fetch upgrade groups for bulk scheduling.
     *
     * Groups orgs by current version and returns offline-computed applicable upgrades.
     */
    async getUpgradeGroups(): Promise<{ currentVersion: string; orgs: IOrg[]; applicableUpgrades: { name: string; upgradeType: 'patch' | 'upgrade'; module: any }[] }[]> {
        const res = await this.http.get(`${this.BASE}/upgrade-groups`);
        return res.data;
    }

    /**
     * Fetch available version filter options for scheduling (updates/upgrades) and module maps.
     */
    async getVersionFilters(): Promise<{
        upgradeVersions: string[];
        updateVersions: string[];
        updateModulesByVersion: Record<string, any>;
        upgradeModulesByVersionName: Record<string, any>;
    }> {
        const res = await this.http.get(`${this.BASE}/version-filters`);
        return res.data;
    }

    /**
     * Start Salesforce OAuth flow for registering an org.
     *
     * @param createdBy - CQ Mgmt username persisted to org audit fields.
     * @param loginUrl - Optional Salesforce login URL.
     * @param tagNames - Optional comma-separated tag names.
     */
    async startOAuthFlow(createdBy: string, loginUrl?: string, tagNames?: string): Promise<{ authorizationUrl: string; state: string }> {
        const qs = new URLSearchParams();
        if (loginUrl) qs.set('loginUrl', loginUrl);
        if (tagNames) qs.set('tagNames', tagNames);
        qs.set('createdBy', createdBy);
        
        // Dynamically construct callback URL pointing to BACKEND
        // The backend handles the OAuth callback, then redirects to frontend
        const currentLocation = window.location;
        const protocol = currentLocation.protocol; // http: or https:
        // Extract backend URL from BASE constant (e.g., http://localhost:8088)
        const backendUrl = new URL(this.http.defaults.baseURL || '');
        const callbackUrl = `${protocol}//${backendUrl.host}/push-portal/auth/callback`;
        qs.set('callbackUrl', callbackUrl);
        
        const params = `?${qs.toString()}`;
        const res = await this.http.get(`${this.BASE}/auth/start${params}`);
        return res.data;
    }

    /**
     * Exchange OAuth authorization code for an authenticated org record.
     * `createdBy` is included so the backend can persist the org audit field.
     */
    async exchangeOAuthCode(code: string, state: string, createdBy: string): Promise<IOrg> {
        const res = await this.http.post(`${this.BASE}/auth/exchange`, { code, state, createdBy });
        return res.data;
    }

    /**
     * Sync installed package versions for an org.
     *
     * Used by UI to refresh package info and to drive upgrade scheduling.
     */
    async syncInstalledPackages(orgId: string): Promise<Record<string, string>> {
        const res = await this.http.post(`${this.BASE}/orgs/${orgId}/sync-packages`);
        return res.data.installedPackages;
    }

    /** Abort a pending/in-progress activity by setting its status to Aborted. */
    async abortActivity(orgId: string): Promise<void> {
        await this.http.patch(`${this.BASE}${CQAppConstant.PUSH_PORTAL_UI.ENDPOINTS.ABORT_ACTIVITY}`, { orgId });
    }
}

export const PushPortalServiceProvider = new PushPortalService();
